import { Grid, Typography, Divider, Box, Paper } from "@mui/material";
import { useIntl } from "react-intl";
import { Spinner } from "react-bootstrap";
import MDBox from "components/MDBox";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { Dispatch, SetStateAction } from "react";
import {
  IconReceipt,
  IconUser,
  IconUserCog,
  IconBuildingBank,
  IconBuilding,
  IconShoppingCart,
  IconFileDollar,
  IconFileCheck,
} from "@tabler/icons-react";

interface EstadisticaItem {
  actual: number;
  anterior: number;
  porcentaje: number;
  tendencia: "subida" | "bajada" | "igual";
  total_global: number;
}

interface EstadisticasData {
  periodo: string;
  total_requisiciones: EstadisticaItem;
  pendientes_jefe_inmediato: EstadisticaItem;
  pendientes_director_area: EstadisticaItem;
  pendientes_daf: EstadisticaItem;
  pendientes_direccion_general: EstadisticaItem;
  pendientes_compra: EstadisticaItem;
  cotizando_sin_orden_compra: EstadisticaItem;
  pendientes_de_autorizar: EstadisticaItem;
}

interface EstadisticasModuloProps {
  verEstadisticas?: EstadisticasData | null;
  usuarioArjion?: string | null;
  procesando?: boolean;
  getVerDetalleEstadistica: (datos: any) => Promise<void>;
  setTipoQuery: Dispatch<SetStateAction<string>>;
  idUsuario: number;
}

const EstadisticasModulo = ({
  verEstadisticas,
  usuarioArjion,
  procesando,
  setTipoQuery,
  getVerDetalleEstadistica,
  idUsuario
}: EstadisticasModuloProps) => {
  const intl = useIntl();

  if (!verEstadisticas && !procesando) return null;

  type CardColor =
    | "dark"
    | "info"
    | "warning"
    | "error"
    | "primary"
    | "success"
    | "secondary"
    | "white";

  const getPorcentaje = (
    item: EstadisticaItem
  ): { color: CardColor; amount: string; label: string } => {
    const color: CardColor =
      item.tendencia === "subida" ? "success" : item.tendencia === "bajada" ? "error" : "warning";

    const simbolo = item.tendencia === "subida" ? "+" : item.tendencia === "bajada" ? "-" : "";

    const labelPeriodo =
      verEstadisticas?.periodo === "semana"
        ? "que la semana anterior"
        : verEstadisticas?.periodo === "mes"
          ? "que el mes anterior"
          : "que el año anterior";

    return {
      color,
      amount: `${simbolo}${item.porcentaje}%`,
      label: labelPeriodo,
    };
  };

  const tarjetas = [
    {
      title: "Requisiciones totales",
      icon: IconReceipt,
      colorHexa: "#041d45",
      color: "dark",
      key: "total_requisiciones",
    },
    {
      title: (
        <>
          Requisiciones Pendientes
          <br />
          Jefe Inmediato
        </>
      ),
      icon: IconUser,
      colorHexa: "#041d45",
      color: "info",
      key: "pendientes_jefe_inmediato",
    },
    {
      title: (
        <>
          Requisiciones Pendientes
          <br />
          Director de Área
        </>
      ),
      icon: IconUserCog,
      colorHexa: "#041d45",
      color: "warning",
      key: "pendientes_director_area",
    },
    {
      title: (
        <>
          Requisiciones Pendientes
          <br />
          DAF
        </>
      ),
      icon: IconBuildingBank,
      colorHexa: "#041d45",
      color: "error",
      key: "pendientes_daf",
    },
    {
      title: (
        <>
          Requisiciones Pendientes
          <br />
          Dirección General
        </>
      ),
      icon: IconBuilding,
      colorHexa: "#041d45",
      color: "primary",
      key: "pendientes_direccion_general",
    },
    {
      title: (
        <>
          Requisiciones Pendientes
          <br />
          de Compra
        </>
      ),
      icon: IconShoppingCart,
      colorHexa: "#041d45",
      color: "success",
      key: "pendientes_compra",
    },
    {
      title: "Requisiciones Cotizando",
      icon: IconFileDollar,
      colorHexa: "#041d45",
      color: "secondary",
      key: "cotizando_sin_orden_compra",
    },
    {
      title: "Pendientes de autorizar",
      icon: IconFileCheck,
      colorHexa: "#041d45",
      color: "secondary",
      key: "pendientes_de_autorizar",
    },
  ] as const;

  return (
    <Box sx={{ px: 2, pb: 2, mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} md={12}>
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{ color: "#004c93", textAlign: { xs: "center" } }}
              >
                Estadísticas generales
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />

          {procesando ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({ id: "general_cargando_datos" })}
              </Typography>
              <Spinner animation="border" />
            </Box>
          ) : verEstadisticas ? (
            <Grid container spacing={3} mt={2}>
              {tarjetas.map(({ title, icon: TarjetaIcon, colorHexa, color, key }) => {
                const item = verEstadisticas[key];
                return (
                  <Grid item xs={12} md={6} lg={3} key={key}>
                    <MDBox mb={1.5}>
                      <ComplexStatisticsCard
                        tituloOjo={"Ver requisiciones"}
                        color={color as any}
                        colorHexa={colorHexa}
                        icon={<TarjetaIcon size={36} stroke={2} />}
                        title={title}
                        count={item.total_global ?? 0}
                        percentage={getPorcentaje(item)}
                        onEyeClick={() => {
                          const datos = {
                            tipo_query: key,
                            id_usuario: idUsuario
                          };
                          //console.log(datos);
                          setTipoQuery(key);
                          getVerDetalleEstadistica(datos);
                        }}
                      />
                    </MDBox>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography variant="h6" color="text.secondary">
                {intl.formatMessage({ id: "no_hay_datos_registrados" })}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default EstadisticasModulo;
